<template>
    <div class="tw:bg-slate-50/70 tw:border-t tw:border-slate-200 tw:p-4" :data-cy="`resumen-ventas-sesion-${props.sesion.id}`">
        <div class="tw:flex tw:flex-col tw:gap-3 tw:lg:flex-row tw:lg:items-center tw:lg:justify-between">
            <div class="tw:flex tw:flex-wrap tw:items-center tw:gap-2">
                <Pill
                    v-if="props.sesion.fecha_inicio"
                    variant="amber"
                    icon="fas fa-clock"
                    :label="props.sesion.fecha_inicio"
                />
                <span v-if="props.sesion.codigo" class="tw:text-xs tw:font-semibold tw:text-slate-500">
                    {{ props.sesion.codigo }}
                </span>
                <span
                    v-if="textoSesion"
                    data-cy="resumen-ventas-sesion-texto"
                    class="tw:text-sm tw:font-semibold tw:text-slate-700"
                >{{ textoSesion }}</span>
            </div>

            <div class="tw:flex tw:flex-wrap tw:items-center tw:gap-2">
                <span class="tw:inline-flex tw:items-center tw:gap-2 tw:rounded-lg tw:border tw:border-slate-200 tw:bg-white tw:px-3 tw:py-1">
                    <span class="tw:text-xs tw:text-slate-500">Recaudación sesión:</span>
                    <span class="tw:text-sm tw:font-bold tw:text-blue-700">{{ importe(importeSesion) }}</span>
                    <span class="tw:rounded tw:bg-emerald-100 tw:px-2 tw:py-0.5 tw:text-xs tw:font-semibold tw:text-emerald-800">
                        {{ entradasSesion }} {{ entradasSesion === 1 ? 'entrada total' : 'entradas totales' }}
                    </span>
                </span>

                <Button
                    variant="default"
                    icon="fas fa-receipt"
                    :custom-class="'tw:justify-center'"
                    :data-cy="`resumen-ventas-ventas-${props.sesion.id}`"
                    @click="$emit('toggle-ventas', props.sesion)"
                >
                    Ventas
                    <i :class="['fas', props.abierto ? 'fa-chevron-up' : 'fa-chevron-down']"></i>
                </Button>
            </div>
        </div>

        <div v-if="props.abierto" class="tw:flex tw:flex-col tw:gap-3 tw:mt-3">
            <div class="tw:grid tw:grid-cols-1 tw:gap-3 tw:sm:grid-cols-3">
                <div class="tw:rounded-lg tw:border tw:border-blue-200 tw:bg-blue-50/60 tw:p-3">
                    <div class="tw:flex tw:items-center tw:justify-between tw:gap-2 tw:mb-1">
                        <span class="tw:text-xs tw:font-semibold tw:text-blue-700">Ventas taquilla</span>
                        <i class="fas fa-store tw:text-blue-600"></i>
                    </div>
                    <div class="tw:text-lg tw:font-bold tw:text-blue-700">{{ importe(canal('taquilla').importe) }}</div>
                    <div class="tw:text-xs tw:font-medium tw:text-blue-600">{{ canal('taquilla').entradas }} ent. vendidas</div>
                </div>

                <div class="tw:rounded-lg tw:border tw:border-slate-200 tw:bg-white tw:p-3">
                    <div class="tw:flex tw:items-center tw:justify-between tw:gap-2 tw:mb-1">
                        <span class="tw:text-xs tw:font-semibold tw:text-slate-500">Ventas online</span>
                        <i class="fas fa-globe tw:text-slate-400"></i>
                    </div>
                    <div class="tw:text-lg tw:font-bold tw:text-slate-800">{{ importe(canal('online').importe) }}</div>
                    <div class="tw:text-xs tw:text-slate-500">{{ canal('online').entradas }} ent. vendidas</div>
                </div>

                <div class="tw:rounded-lg tw:border tw:border-slate-200 tw:bg-white tw:p-3">
                    <div class="tw:flex tw:items-center tw:justify-between tw:gap-2 tw:mb-1">
                        <span class="tw:text-xs tw:font-semibold tw:text-slate-500">Invitaciones</span>
                        <i class="fas fa-ticket-alt tw:text-slate-400"></i>
                    </div>
                    <div class="tw:text-lg tw:font-bold tw:text-slate-800">{{ importe(canal('invitaciones').importe) }}</div>
                    <div class="tw:text-xs tw:text-slate-500">{{ canal('invitaciones').entradas }} invitaciones</div>
                </div>
            </div>

            <DesgloseZonasTable
                :desglose="props.estado.desglose || []"
                :loading="!!props.estado.cargandoDesglose"
                :error="props.estado.desgloseError || null"
                @reintentar="$emit('reintentar-desglose', props.sesion)"
            />
        </div>
    </div>
</template>

<script setup>
    import appFilters from '@/filters';
    import { computed } from 'vue';

    import Button from '@/modules/admin/ui/Button.vue';
    import Pill from '@/modules/admin/ui/Pill.vue';
    import DesgloseZonasTable from '@/modules/admin/eventos/resumen-ventas/components/DesgloseZonasTable.vue';
    import { canalDe, importeFila, entradasFila } from '@/composables/admin/eventos/resumenVentasAgregados';

    const props = defineProps({
        /** Sesión del endpoint de sesiones del evento. */
        sesion: {
            type: Object,
            required: true
        },
        /** Estado de su desglose: cargandoDesglose, desglose, desgloseError. */
        estado: {
            type: Object,
            default: () => ({})
        },
        /** `true` si el panel de ventas de la sesión está desplegado. */
        abierto: {
            type: Boolean,
            default: false
        }
    });

    defineEmits(['toggle-ventas', 'reintentar-desglose']);

    const importe = appFilters.formatear_precio;

    const importeSesion = computed(() => importeFila(props.sesion));
    const entradasSesion = computed(() => entradasFila(props.sesion));

    /**
     * `nombre_completo` del endpoint es "<fecha>, <texto de la sesión>". La
     * fecha ya se pinta en la pill, así que aquí se recorta: sin esto la misma
     * fecha aparece dos veces seguidas y el texto de la sesión queda detrás.
     * Si la sesión no tiene texto, el recorte deja cadena vacía y el span no
     * se pinta.
     */
    const textoSesion = computed(() => {
        const nombre = props.sesion.nombre_completo || '';
        const fecha = props.sesion.fecha_inicio || '';

        if (!nombre || !fecha) return nombre;

        const prefijo = `${fecha},`;

        return nombre.startsWith(prefijo) ? nombre.slice(prefijo.length).trim() : nombre;
    });

    /**
     * @param {string} nombre 'taquilla' | 'online' | 'invitaciones'
     * @returns {{ entradas: number, importe: number }}
     */
    function canal(nombre) {
        return canalDe(props.sesion, nombre);
    }
</script>
